import { useMemo, useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { getToken } from "@/api/auth-api"
import { exportPersonalizationApi } from "@/api/home-context"
import { getMemoriesApi, getPrismClaimsApi } from "@/api/memory"
import { Download, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useHomeContext } from "@/features/chat/hooks/use-home-context"
import { TrustedSourcesDialog } from "@/features/trusted-sources/components/trusted-sources-dialog"
import { usePersonalizationWipe } from "@/hooks/use-personalization-wipe"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

function getSettingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

export type WhatWeKnowPanelProps = {
  active: boolean
  onSwitchTab?: (tab: "general" | "about-me" | "memory" | "what-we-know") => void
}

export function WhatWeKnowPanel({ active, onSwitchTab }: WhatWeKnowPanelProps) {
  const prismMemory = usePrismMemory()
  const { t } = useTranslation()
  const { instance } = useMsal()

  const homeContext = useHomeContext(active)
  const profile = homeContext.data?.helloai.user_profile ?? null
  const userInstructions = homeContext.data?.helloai.user_instructions ?? null
  const people = homeContext.data?.graph.people ?? []
  const settingsService = useMemo(() => {
    try {
      return SettingsService.getInstance()
    } catch {
      return null
    }
  }, [])

  const memoriesQuery = useQuery({
    queryKey: ["memories"],
    queryFn: async () => {
      const tokenResult = await getToken(instance)
      return getMemoriesApi(tokenResult?.accessToken)
    },
    enabled: active && !prismMemory,
  })

  const ltmCount = prismMemory ? 0 : (memoriesQuery.data?.ltm.length ?? 0)
  const ltmPreview = prismMemory ? [] : (memoriesQuery.data?.ltm.slice(0, 3) ?? [])
  const prismClaims = useInfiniteQuery({
    queryKey: ["prism-memory", "claims"],
    queryFn: async ({ pageParam }) =>
      getPrismClaimsApi((await getToken(instance))?.accessToken, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: active && prismMemory,
    retry: false,
  })
  const claimPreview = prismClaims.isError
    ? []
    : (prismClaims.data?.pages.flatMap((page) => page.claims).slice(0, 3) ?? [])
  let claimsStatusKey = "personalization.memoryPanel.prism.claimsDescription"
  if (prismClaims.isError) claimsStatusKey = "personalization.memoryPanel.prism.errors.unavailable"
  else if (prismClaims.isPending) claimsStatusKey = "common.loading"

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showTrustedSourcesDialog, setShowTrustedSourcesDialog] = useState(false)

  const { wipeMutation, removalPending, removalAwaitingPrism, removalStatusError } =
    usePersonalizationWipe(getSettingsToken, () => {
      setConfirmOpen(false)
    })

  async function handleExport() {
    if (exporting) return
    setExporting(true)
    try {
      const idToken = getSettingsToken()
      const {
        blob,
        filename,
        complete,
        incomplete_categories: incompleteCategories,
      } = await exportPersonalizationApi(idToken)
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      const date = new Date().toISOString().slice(0, 10)
      a.download = filename ?? `hello-ai-data-${date}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      if (!complete) {
        // File still downloads — same partial-success posture as wipe — but
        // toast so a DSAR export isn't silently missing categories.
        toast.error(
          t("personalization.whatWeKnow.danger.exportPartialSuccess", {
            defaultValue:
              "Export downloaded, but some categories could not be fully retrieved ({{categories}}). Please try again.",
            categories: incompleteCategories.join(", ") || "unknown",
          })
        )
      } else {
        toast.success(
          t("personalization.whatWeKnow.danger.exportSuccess", {
            defaultValue: "Data exported",
          })
        )
      }
    } catch (error) {
      logger.error("export failed", error as Error)
      toast.error(t("personalization.whatWeKnow.danger.exportFailed"))
    } finally {
      setExporting(false)
    }
  }

  const profileName =
    profile?.name ?? settingsService?.claims.name ?? t("personalization.whatWeKnow.profile.notSet")
  const jobTitle = profile?.job_title ?? t("personalization.whatWeKnow.profile.notSet")
  const department = profile?.department ?? t("personalization.whatWeKnow.profile.notSet")
  const manager = profile?.manager_name ?? t("personalization.whatWeKnow.profile.notSet")

  const aboutMePreview = (() => {
    if (!userInstructions || userInstructions.trim().length === 0) {
      return null
    }
    if (userInstructions.length > 240) {
      return userInstructions.slice(0, 240) + "…"
    }
    return userInstructions
  })()

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.profile.title")}
          </CardTitle>
          <CardDescription>{t("personalization.whatWeKnow.profile.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              {t("personalization.whatWeKnow.profile.name")}
            </span>
            <span className="text-right">{profileName}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              {t("personalization.whatWeKnow.profile.jobTitle")}
            </span>
            <span className="text-right">{jobTitle}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              {t("personalization.whatWeKnow.profile.department")}
            </span>
            <span className="text-right">{department}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              {t("personalization.whatWeKnow.profile.manager")}
            </span>
            <span className="text-right">{manager}</span>
          </div>
          {profile?.fetched_at ? (
            <div className="text-muted-foreground pt-2 text-xs">
              {t("personalization.whatWeKnow.profile.lastSynced")}:{" "}
              {new Date(profile.fetched_at).toLocaleString()}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.aboutMe.title")}
          </CardTitle>
          <CardDescription>{t("personalization.whatWeKnow.aboutMe.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          {aboutMePreview ? (
            <p className="whitespace-pre-wrap">{aboutMePreview}</p>
          ) : (
            <p className="text-muted-foreground">{t("personalization.whatWeKnow.aboutMe.empty")}</p>
          )}
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={() => onSwitchTab?.("about-me")}>
              {t("personalization.whatWeKnow.aboutMe.edit")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.memory.title")}
          </CardTitle>
          <CardDescription>{t("personalization.whatWeKnow.memory.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-muted-foreground">
            {prismMemory
              ? t(claimsStatusKey)
              : t("personalization.whatWeKnow.memory.count", { count: ltmCount })}
          </p>
          {prismMemory && claimPreview.length > 0 && (
            <ul className="list-disc space-y-1 pl-5">
              {claimPreview.map((item) => (
                <li key={item.claim_uid}>
                  {item.text}
                  {item.imported && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {t("personalization.memoryPanel.prism.importedBadge")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {ltmPreview.length > 0 && (
            <ul className="list-disc space-y-1 pl-5">
              {ltmPreview.map((item) => (
                <li key={item.id}>{item.text}</li>
              ))}
            </ul>
          )}
          <div className="pt-2">
            <Button size="sm" variant="outline" onClick={() => onSwitchTab?.("memory")}>
              {t("personalization.whatWeKnow.memory.viewAll")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.collaborators.title")}
          </CardTitle>
          <CardDescription>
            {t("personalization.whatWeKnow.collaborators.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          {people.length === 0 ? (
            <p className="text-muted-foreground">
              {t("personalization.whatWeKnow.collaborators.empty")}
            </p>
          ) : (
            <ul className="space-y-1">
              {people.slice(0, 5).map((p) => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.savedPrompts.title")}
          </CardTitle>
          <CardDescription>{t("personalization.whatWeKnow.savedPrompts.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {t("personalization.whatWeKnow.savedPrompts.unknown")}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("personalization.whatWeKnow.trustedSources.title")}
          </CardTitle>
          <CardDescription>
            {t("personalization.whatWeKnow.trustedSources.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="sm" variant="outline" onClick={() => setShowTrustedSourcesDialog(true)}>
            {t("personalization.whatWeKnow.trustedSources.manage")}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive text-base">
            {t("personalization.whatWeKnow.danger.title")}
          </CardTitle>
          <CardDescription>{t("personalization.whatWeKnow.danger.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row">
          {removalPending && (
            <p className="text-muted-foreground text-sm" role="status">
              {t("personalization.whatWeKnow.danger.wipeInProgress")}
            </p>
          )}
          {removalAwaitingPrism && (
            <p className="text-muted-foreground text-sm" role="status">
              {t("personalization.whatWeKnow.danger.wipeAwaitingPrism")}
            </p>
          )}
          {removalStatusError && (
            <p className="text-destructive text-sm" role="alert">
              {t("personalization.whatWeKnow.danger.wipeStatusStopped")}
            </p>
          )}
          <Button
            data-testid="export-button"
            disabled={exporting}
            size="sm"
            variant="outline"
            onClick={() => {
              void handleExport()
            }}
          >
            <Download className="mr-2 size-4" />
            {exporting
              ? t("personalization.whatWeKnow.danger.exporting")
              : t("personalization.whatWeKnow.danger.exportButton")}
          </Button>
          <Button
            data-testid="wipe-button"
            disabled={removalPending || wipeMutation.isPending}
            size="sm"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 className="mr-2 size-4" />
            {t("personalization.whatWeKnow.danger.wipeButton")}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("personalization.whatWeKnow.danger.wipeDialogTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("personalization.whatWeKnow.danger.wipeDialogDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("personalization.whatWeKnow.danger.wipeCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removalPending || wipeMutation.isPending}
              onClick={(e) => {
                e.preventDefault()
                wipeMutation.mutate()
              }}
            >
              {t("personalization.whatWeKnow.danger.wipeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TrustedSourcesDialog
        open={showTrustedSourcesDialog}
        onOpenChange={setShowTrustedSourcesDialog}
      />
    </div>
  )
}
